import { useState, useEffect, useCallback } from "react";
import DropDown from "../../elements/Dropdown";
import PieChartComponent from "../../elements/PieChart";
import ProductCard from "../../elements/ProductCard";
import StackedBarChart from "../../elements/StackedBarChart";
import styles from "./index.module.css";
import MultiselectDropdown from "../../elements/MultiselectDropdown";
import {
  fnbStore,
  productData,
  rowCounts,
  sortingMapping,
  sortingSteps,
  storeData,
  storeSteps,
  terminalSteps,
} from "../../../utils/constant";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../elements/Header";
import { useSelector } from "react-redux";
import { fetchFnbProducts } from "../../../store/slices/fnb/products.slice";
import { useDispatch } from "react-redux";
import PulseLoader from "react-spinners/PulseLoader";
import BeatLoader from "react-spinners/BeatLoader";

const debounce = (callback) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      callback(...args);
    }, 400);
  };
};

const getPriceAverage = (products) => {
  const temp = products.reduce(
    (total, product) => (total += Number(product.latest_price)),
    0
  );
  return temp / products.length;
};

const FnbProducts = (props) => {
  const dispatch = useDispatch();
  const [sorting, setSorting] = useState(0);
  const [products, setProducts] = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState([]);
  const [selectedStore, setSelectedStore] = useState([]);
  const [selectedBreakup, setSelectedBreakup] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(2);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedPeriodLabel, setSelectedPeriodLabel] = useState('February 2026');
  const [search, setSearch] = useState("");
  const [fullscreen, setFullscreen] = useState(false);
  const [reload, setReload] = useState(false);
  const searchParams = useSearchParams();
  const navigate = useNavigate();
  const [checkURL, setCheckURL] = useState(false);
  const {
    productsData: { stats, data, totals: totalRecords },
    loading,
  } = useSelector((store) => store.fnbProducts);

  const [selPage, setSelPage] = useState(0);
  const [selRowCount, setSelRowCount] = useState(0);

  const {
    data: { store, terminal },
    loading: constantLoading,
  } = useSelector((store) => store.fnbConstants);

  const getProductsData = ({
    store = selectedStore.join("|").toUpperCase(),
    breakup = selectedBreakup.join("|").toUpperCase(),
    terminal = selectedTerminal.join("|").toUpperCase(),
    search = "",
    sort = sortingMapping[sortingSteps[sorting]],
    limit = rowCounts[selRowCount],
    offset = selPage * Number(rowCounts[selRowCount]),
    pricerange,
    month = selectedMonth,
    year = selectedYear,
  } = {}) => {
    dispatch(
      fetchFnbProducts({
        store,
        terminal,
        search,
        sort,
        limit,
        offset,
        pricerange,
        breakup,
        month,
        year,
      })
    );
  };

  useEffect(() => {
    if (loading) {
      setSelectedTerminal(terminal);
      setSelectedStore(store);
      setSelectedBreakup(store);
    }
  }, [store, terminal]);

  // Format products data.
  useEffect(() => {
    let formatedData = [];
    data.forEach((product) => {
      let formatProduct = {
        id: product.canprod_id,
        store: product.store_name,
        terminal_name: product.terminal_name,
        title: product.product_name,
        priceRank: product.products_data.find(p=>p.store_name==product.store_name)?product.products_data.find(p=>p.store_name==product.store_name).pricerank:"NA",
        price: product.store_price,
        average: product.average,
        diffPercentage: (Number(product.store_price) - Number(product.average))/Number(product.store_price) * 100,
        difference:Number(product.store_price) - Number(product.average),
          // ((Number(product.store_price) - Number(product.average)) *
          //   Number(product.store_price)) /
          // 100,
        subProducts: [],
      };

      product.products_data.forEach((item) => {
        let formatSubProduct = {
          title: item.name,
          store: item.store_name,
          terminal_name: item.terminal_name,
          price: item.latest_price,
          priceDifference: product.store_price - item.latest_price,
          notes: item.note,
        };
        formatProduct.subProducts.push(formatSubProduct);
      });
      formatProduct.subProducts.sort((a, b) => a.price - b.price);

      formatedData.push(formatProduct);
    });

    setProducts(formatedData);
  }, [data]);

  const toggleReload = () => setReload((value) => !value);

  // Check the url query params.
  useEffect(() => {
    if (searchParams[0]?.get("terminal")) {
      setSelectedTerminal([searchParams[0]?.get("terminal")]);
    }
    if (searchParams[0]?.get("store")) {
      const storeParam = searchParams[0]?.get("store");
      console.log("Store parameter from URL:", storeParam);
      setSelectedStore([storeParam]);
    }
    if (searchParams[0]?.get("breakup")) {
      setSelectedBreakup([searchParams[0]?.get("breakup")]);
    }
    setCheckURL(true);
  }, []);

  const debounceGetAllData = useCallback(debounce(getProductsData), []);

  useEffect(() => {
    if (checkURL) {
      const store = selectedStore.join("|").toUpperCase();
      const breakup = selectedBreakup.join("|").toUpperCase();
      const terminal = selectedTerminal.join("|").toUpperCase();
      const sort = sortingMapping[sortingSteps[sorting]];
      const pricerange = searchParams[0]?.get("label");
      debounceGetAllData({ store, terminal, search, sort, pricerange, breakup, month: selectedMonth, year: selectedYear });
    }
  }, [sorting, selectedTerminal, selectedStore, selectedBreakup, search, reload, checkURL, selectedMonth, selectedYear]);

  const onSortingChange = (value, index) => setSorting(index);
  const onSelectedTerminalsChange = (value) => {
    setSelectedTerminal(value);
  };
  const onSelectedStoreChange = (value) => {
    setSelectedStore(value);
  };
  const onSelectedBreakupChange = (value) => {
    setSelectedBreakup(value);
  };

  const handleMonthYearChange = (month, year, label) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setSelectedPeriodLabel(label);
    setSelPage(0); // Reset to first page when changing period
  };

  const handleSearch = (e) => setSearch(e?.target?.value);
  const toggleFullscreen = (e) => setFullscreen((value) => !value);

  const getTerminalSelSteps = () => {
    if (searchParams[0]?.get("terminal"))
      return [searchParams[0]?.get("terminal")];
    return terminal;
  };
  const getStoreSelSteps = () => {
    if (searchParams[0]?.get("store")) {
      const storeParam = searchParams[0]?.get("store");
      console.log("Available store options:", store);
      console.log("Selected store from URL:", storeParam);
      console.log("Store options include selected:", store.includes(storeParam));
      return [storeParam];
    }
    return store;
  };

  const redirectToProducts = (e) => {
    let label = e?.currentTarget?.dataset?.label;
    // if(searchParams[0]?.get("label")?.includes(label)) return  navigate(`/dashboard/fnb-products?label=${searchParams[0]?.get("label")?.replace(label,"")}`);
    // return navigate(`/dashboard/fnb-products?label=${(searchParams[0]?.get("label") || "")+label}`);
    navigate("/dashboard/fnb-products?label=" + label);
    toggleReload();
  };

  const handlePage = (page) => {
    getProductsData({
      store: selectedStore.join("|").toUpperCase(),
      breakup: selectedBreakup.join("|").toUpperCase(),
      terminal: selectedTerminal.join("|").toUpperCase(),
      search,
      sort: sortingMapping[sortingSteps[sorting]],
      limit: rowCounts[selRowCount],
      offset: page * rowCounts[selRowCount],
      pricerange: searchParams[0]?.get("label")
    });
  };

  const onPageNoChange = (e) => {
    handlePage(e?.currentTarget?.dataset?.idx);
    setSelPage(e?.currentTarget?.dataset?.idx);
  };

  const skipPages = (e) => {
    handlePage((parseInt(selPage / 5) + 1) * 5);
    setSelPage((parseInt(selPage / 5) + 1) * 5);
  };

  const skipPagesReverse = (e) => {
    handlePage((parseInt(selPage / 5) - 1) * 5);
    setSelPage((parseInt(selPage / 5) - 1) * 5);
  };

  // Row count selection.
  const onSelectedRowCount = (value, index) => {
    setSelRowCount(index);
    getProductsData({
      limit: rowCounts[index],
      offset: selPage * rowCounts[index],
    });
  };
  return (
    <div className={styles["container"]}>
      <Header 
        onMonthYearChange={handleMonthYearChange}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
      />
      <hr />
      <div className={styles["stats"]}>
        <div
          className={styles["box"]}
          onClick={redirectToProducts}
          data-label=""
        >
          <div className={styles["label"]}>Stores</div>
          <div className={styles["value_box"]}>
            <div className={styles["value"]}>
              {loading ? <PulseLoader size={10} /> : stats.stores}
            </div>
            {/* <div className={styles["change"]+" "+styles["up"]}>
                        <div>2</div>
                        <div><i class="bi bi-arrow-up-right"></i></div>
                    </div> */}
          </div>
        </div>
        <div
          className={styles["box"]}
          onClick={redirectToProducts}
          data-label=""
        >
          <div className={styles["label"]}>Products</div>
          <div className={styles["value_box"]}>
            <div className={styles["value"]}>
              {" "}
              {loading ? <PulseLoader size={10} /> : stats.productCount}
            </div>
            {/* <div className={styles["change"]+" "+((changeStats?.cc+changeStats?.ec+changeStats?.mc)>0?styles["up"]:styles["down"])}>
                        <div>{Math.abs(changeStats?.cc + changeStats?.mc + changeStats?.ec)}</div>
                        <div><i class={`bi bi-arrow-${(changeStats?.cc + changeStats?.mc + changeStats?.ec)>0?"up":"down"}-right`}></i></div>
                    </div> */}
          </div>
        </div>
        <div
          className={
            searchParams[0]?.get("label")?.includes("cheapest")
              ? styles["box"] + " " + styles["active"]
              : styles["box"]
          }
          onClick={redirectToProducts}
          data-label="cheapest"
        >
          <div className={styles["label"]}>Cheapest Products</div>
          <div className={styles["value_box"]}>
            <div className={styles["value"]}>
              {" "}
              {loading ? <PulseLoader size={10} /> : stats.cheapestProducts}
            </div>
            {/* <div className={styles["change"]+" "+(changeStats?.cc>0?styles["up"]:styles["down"])}>
                        <div>{Math.abs(changeStats?.cc)}</div>
                        <div><i class={`bi bi-arrow-${changeStats?.cc>0?"up":"down"}-right`}></i></div>
                    </div> */}
          </div>
        </div>
        <div
          className={
            searchParams[0]?.get("label")?.includes("midrange")
              ? styles["box"] + " " + styles["active"]
              : styles["box"]
          }
          onClick={redirectToProducts}
          data-label="midrange"
        >
          <div className={styles["label"]}>Midrange Products</div>
          <div className={styles["value_box"]}>
            <div className={styles["value"]}>
              {loading ? <PulseLoader size={10} /> : stats.midrangeProducts}
            </div>
            {/* <div className={styles["change"]+" "+(changeStats?.mc>0?styles["up"]:styles["down"])}>
                        <div>{Math.abs(changeStats?.mc)}</div>
                        <div><i class={`bi bi-arrow-${changeStats?.mc>0?"up":"down"}-right`}></i></div>
                    </div> */}
          </div>
        </div>
        <div
          className={
            searchParams[0]?.get("label")?.includes("expensive")
              ? styles["box"] + " " + styles["active"]
              : styles["box"]
          }
          onClick={redirectToProducts}
          data-label="expensive"
        >
          <div className={styles["label"]}>Expensive products</div>
          <div className={styles["value_box"]}>
            <div className={styles["value"]}>
              {" "}
              {loading ? <PulseLoader size={10} /> : stats.expensiveProducts}
            </div>
            {/* <div className={styles["change"]+" "+(changeStats?.ec>0?styles["up"]:styles["down"])}>
                        <div>{Math.abs(changeStats?.ec)}</div>
                        <div><i class={`bi bi-arrow-${changeStats?.ec>0?"up":"down"}-right`}></i></div>
                    </div> */}
          </div>
        </div>
      </div>
      <hr />
      <div
        className={
          styles["store"] + " " + (fullscreen ? styles["fullscreen"] : "")
        }
      >
        <div className={styles["store_head"]}>
          <div className={styles["store_title"]}>
            <div>Product List</div>
            <div>Showing {products?.length} products</div>
          </div>
          {store.length > 0 && terminal.length > 0 && (
            <div className={styles["store_filters"]}>
              <div className={styles["dropdown"]}>
                <DropDown
                  steps={sortingSteps}
                  selectedValue={sorting}
                  onChange={onSortingChange}
                />
              </div>
              <div className={styles["dropdown"]}>
                <MultiselectDropdown
                  selSteps={getTerminalSelSteps()}
                  steps={terminal}
                  name="terminals"
                  onChange={onSelectedTerminalsChange}
                />
              </div>
              <div className={styles["dropdown"]}>
                <MultiselectDropdown
                  selSteps={getStoreSelSteps()}
                  steps={store}
                  name="stores"
                  onChange={onSelectedStoreChange}
                />
              </div>
              <div className={styles["dropdown"]}>
                <MultiselectDropdown
                  steps={store}
                  selSteps={store}
                  name="comparisons"
                  onChange={onSelectedBreakupChange}
                />
              </div>
              <div className={styles["search"]}>
                <div>
                  <input
                    type="text"
                    placeholder="Product"
                    onChange={handleSearch}
                  />
                  <i class="bi bi-search"></i>
                </div>
              </div>
              <div className={styles["expand"]} onClick={toggleFullscreen}>
                <div>
                  <i class="bi bi-fullscreen"></i>
                </div>
              </div>
              {/* <div className={styles["export"]}>
                        <div>
                            Export
                        </div>
                        <div>
                            <i class="bi bi-download"></i>
                        </div>
                    </div> */}
            </div>
          )}
        </div>

        <div className={styles["store_content"]}>
          <div>
            {loading ? (
              <div className="loader-container">
                <BeatLoader />
              </div>
            ) : (
              products &&
              products?.map((product) => (
                <div className={styles["store_product"]}>
                  <ProductCard {...product} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className={styles["rowCount"]}>
        <div className={styles["count"]}>
          <span>Row Count</span>
          <div className={styles["countDropdown"]}>
            <DropDown
              steps={rowCounts}
              selectedValue={selRowCount}
              onChange={onSelectedRowCount}
            />
          </div>
        </div>

        <div className={styles["pages"]}>
          {selPage < 5 ? <></> : <span onClick={skipPagesReverse}>&lt;</span>}
          {new Array(parseInt(totalRecords / rowCounts[selRowCount]) + 1 || 0)
            .fill(1)
            ?.map((el, idx) =>
              idx >= parseInt(selPage / 5) * 5 &&
              idx < parseInt(selPage / 5 + 1) * 5 ? (
                <span
                  className={selPage == idx ? styles["selected"] : ""}
                  onClick={onPageNoChange}
                  data-idx={idx}
                >
                  {idx + 1}
                </span>
              ) : (
                <></>
              )
            )}
          {totalRecords / rowCounts[selRowCount] <=
          (parseInt(selPage / 5) + 1) * 5 ? (
            <></>
          ) : (
            <span onClick={skipPages}>&gt;</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default FnbProducts;
